// Admin → Customer assignments (managers: the Admin feature): who looks after which customers, as the
// team's sheet says it — a label ("Macys/ Bloomingdale's"), its person, Owner or Backup, a note — and
// the customer codes it covers, linked here from the customers the open lines have and A2000's and
// Deposco's customer lists ("no open lines"; an inactive one only once linked). Three views, the
// person's last one kept in this browser: Needs confirming (labels with no linked code: "Not linked
// yet", suggested codes or none), Assignments (grouped by person, each group closed until opened) and
// No owner (the open lines' customers no Owner has, biggest first, customers sharing a shown name as
// one, as Routing rules). A label's suggested codes (`proposedKeys`, worked out on every read, never
// stored) are linked one by one or all at once — a match by name is never saved by itself. Add takes a
// person alone ("0 customers"), or customers picked by shown name (linked at once) or typed. "Assign…"
// gives one person's customers to another as Backup; "Unassign" retires them. "Load from
// sheet" adds the sheet's rows once, with no code. Each save is a change of its own in the System log;
// never deleted: retired. The rules are `assignments.ts`'s; the server refuses anyone but the Admin feature.

import { Fragment, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, UploadIcon, XIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CUSTOMER_ASSIGNMENTS,
  ROUTING_API,
  activePeople,
  addButtonText,
  addedAssignments,
  addAction,
  addedText,
  alsoLinkedText,
  assignmentCounts,
  assignmentViews,
  codeHolders,
  codesOfPicks,
  countText,
  coverInput,
  coverNote,
  coverPlan,
  coverItemNote,
  coverResultText,
  coverTickedAtFirst,
  coversHeld,
  customerMatches,
  customerPicks,
  emailKey,
  endCoverPrompt,
  unassignNote,
  holdUnlinked,
  holdersHover,
  isEmail,
  isPersonOnly,
  itemSuggestions,
  joinKeys,
  chipsByPlace,
  keysOfPerson,
  keysOfItems,
  linkAllText,
  linkEach,
  linkKeys,
  linkPicks,
  linkTickedText,
  linkedForText,
  linkedHereText,
  listedInNeeds,
  nameAfterEmail,
  noOwnerItems,
  noOwnerLabel,
  offeredToLink,
  openedFrom,
  openingAssignmentsView,
  optionName,
  otherHolders,
  pageNameOf,
  pendingSuggestions,
  personGroupOpen,
  personMatches,
  personOfEmail,
  personOnlyRow,
  picksLeft,
  personSummary,
  saveEach,
  selectAllShown,
  sharedBeforeLinkAll,
  sheetRows,
  sourceOfCustomerKey,
  suggestedForText,
  suggestionHover,
  tickedSuggestions,
  togglePick,
  unassignedCustomers,
  unlinkKeys,
  unlinkedText,
  visiblePicks,
} from "@app/shared";
import type {
  AddMode,
  AssignmentView,
  AssignmentsPayload,
  AssignmentsView,
  CodeHolder,
  CoverItem,
  CustomerAssignmentRecord,
  CustomerOption,
  CustomerPick,
  HeldChips,
  NewAssignment,
  NoOwnerItem,
  PeoplePayload,
  PendingSuggestion,
  PersonOption,
} from "@app/shared";
import {
  Button,
  DataError,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TableSkeleton,
  apiGet,
  crudApi,
  crudErrorText,
  useMe,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useDialogDrag } from "./dialog-drag";
import { useLoggedWrites } from "./logged-write";
import { notify, notifyUndo, useMessage } from "./message";
import { PickChips, PickList, pickRows, usePickNav } from "./pick-list";
import type { PickRow } from "./pick-list";
import { SegmentedViews } from "./segmented";
import { SourceBadge } from "./source-filter";
import { SearchBox } from "./toolbar";
import { useAdminAskTab } from "./axai-admin";
import type { AdminView } from "@app/shared";
import { SelectionBar } from "./selection-bar";

export const ASSIGNMENTS_KEY = ["routing", "admin", "assignments"] as const;
/** Whom Add suggests (`GET /api/routing/admin/people`): the role groups, who signed in, the assignments, the sheet. */
const PEOPLE_KEY = ["routing", "admin", "people"] as const;

type Input = Omit<CustomerAssignmentRecord, "id" | "rv" | "updated_at">;
const inputOf = (r: CustomerAssignmentRecord): Input => ({
  personEmail: r.personEmail,
  personName: r.personName,
  label: r.label,
  customerKeys: r.customerKeys ?? "",
  role: r.role,
  note: r.note ?? "",
  retired: r.retired,
  updatedBy: r.updatedBy ?? "",
});

/** A change to an assignment, worked out from the record as it is now on the server. */
type Patch = Partial<Input> | ((now: Input) => Partial<Input>);

/** Writes of the tab: each a change of its own in the System log; every page reads them again. */
function useAssignmentWrites() {
  const writes = useLoggedWrites();
  const message = useMessage();
  const queryClient = useQueryClient();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const text = (r: Pick<Input, "personName" | "label" | "role">) =>
    r.label.trim() ? `${r.personName}: ${r.label} (${r.role})` : `${r.personName}: no customers yet`;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["routing"] });
  /** One save of `patch` on `row` as it is now (another manager may have saved it since); throws when it fails. */
  const put = async (row: CustomerAssignmentRecord, patch: Patch, note: string) => {
    const now = await crudApi.get(CUSTOMER_ASSIGNMENTS.id, row.id);
    const before = inputOf(now as unknown as CustomerAssignmentRecord);
    const change = typeof patch === "function" ? patch(before) : patch;
    const next = { ...before, ...change, updatedBy: who };
    const changes = (Object.keys(change) as (keyof Input)[])
      .filter((f) => String(before[f] ?? "") !== String(next[f] ?? ""))
      .map((f) => ({ f, from: String(before[f] ?? ""), to: String(next[f] ?? "") }));
    if (!changes.length) return;
    await writes.update(CUSTOMER_ASSIGNMENTS, row.id, String(now.rv), next, {
      action: "edit",
      lineText: text(next),
      changes,
      note,
    });
  };
  const create = async (input: NewAssignment) => {
    await writes.create(
      CUSTOMER_ASSIGNMENTS,
      { ...input, customerKeys: input.customerKeys ?? "", note: input.note ?? "", updatedBy: who },
      {
        action: "edit",
        lineText: text(input),
        changes: [
          { f: "label", from: "", to: input.label },
          ...(input.customerKeys ? [{ f: "customerKeys", from: "", to: input.customerKeys }] : []),
          { f: "role", from: "", to: input.role },
        ],
        note: isPersonOnly(input) ? "Added, no customers yet" : "Added",
      },
    );
  };
  return {
    create,
    /**
     * A new assignment; `into`: the person's person-only record (`personOnlyRow`), which it fills instead
     * of adding another. Throws when it fails.
     */
    add: (input: NewAssignment, into?: CustomerAssignmentRecord) =>
      into
        ? put(into, { ...input, customerKeys: input.customerKeys ?? "", note: input.note ?? "" }, "Added")
        : create(input),
    /** One save; throws when it fails (the caller says it, e.g. for several at once). */
    put,
    /** Saves `patch` on `row`; a failure is the red message, and the list is read again either way. */
    async update(row: CustomerAssignmentRecord, patch: Patch, note = ""): Promise<boolean> {
      try {
        await put(row, patch, note);
        return true;
      } catch (e) {
        await message.error("Couldn't save the assignment", crudErrorText(e), [
          "Someone may have saved it at the same time: the list is read again.",
        ]);
        return false;
      } finally {
        await refresh();
      }
    },
    /** One code linked, added to the keys the record has now — a Link's own save and entry; throws when it fails. */
    link: (row: CustomerAssignmentRecord, key: string) =>
      put(row, (now) => ({ customerKeys: linkKeys(now.customerKeys, [key]) }), `Linked ${key}`),
    refresh,
  };
}

/** The view kept for this person in this browser (as the tables' sorts are: P20). */
const viewKey = (who: string) => `routing.assignments.view.${who}`;
function readView(who: string): unknown {
  try {
    return localStorage.getItem(viewKey(who));
  } catch {
    return null;
  }
}
function writeView(who: string, view: AssignmentsView) {
  try {
    localStorage.setItem(viewKey(who), view);
  } catch {
    // Not kept (a private window, blocked storage): the tab opens on its default next time.
  }
}

/** The people a person opened in a view, kept in this browser like the view; unreadable: none (all closed). */
const openedKey = (who: string, view: AssignmentsView) => `routing.assignments.open.${who}.${view}`;
function readOpened(who: string, view: AssignmentsView): Set<string> {
  try {
    return new Set(openedFrom(localStorage.getItem(openedKey(who, view))));
  } catch {
    return new Set();
  }
}
function writeOpened(who: string, view: AssignmentsView, opened: ReadonlySet<string>) {
  try {
    localStorage.setItem(openedKey(who, view), JSON.stringify([...opened]));
  } catch {
    // Not kept: the groups start closed next time.
  }
}

export function CustomerAssignmentsTab() {
  const message = useMessage();
  const w = useAssignmentWrites();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.username : "";
  const [person, setPerson] = useState("");
  const [search, setSearch] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState<AssignmentsView | null>(null);
  // In this visit, per row: the codes linked ("Linked ✓ · Undo" where its suggestions were; in Needs
  // confirming it stays listed) and the chips held as they were before an unlink (struck through, in
  // place). Every view: nothing moves under the pointer until the view changes or the page loads.
  const [linkedHere, setLinkedHere] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  const [held, setHeld] = useState<ReadonlyMap<string, HeldChips>>(new Map());
  const data = useQuery({
    queryKey: ASSIGNMENTS_KEY,
    queryFn: ({ signal }) => apiGet<AssignmentsPayload>(`${ROUTING_API}/admin/assignments`, signal),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const all = useMemo(() => data.data?.assignments ?? [], [data.data]);
  const customers = useMemo(() => data.data?.customers ?? [], [data.data]);
  const byKey = useMemo(() => new Map(customers.map((c) => [c.key, c])), [customers]);
  const views = useMemo(() => assignmentViews(all, customers), [all, customers]);
  const holders = useMemo(() => codeHolders(all), [all]);
  const pending = useMemo(() => pendingSuggestions(views), [views]);
  const counts = assignmentCounts(views, customers);
  const people = [...new Map(all.map((a) => [emailKey(a.personEmail), a.personName])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );
  const view = chosen ?? openingAssignmentsView(readView(who), counts.needs);
  const choose = (v: AssignmentsView) => {
    setChosen(v);
    writeView(who, v);
    setLinkedHere(new Map());
    setHeld(new Map());
  };
  const q = search.trim().toLowerCase();
  const kept = (v: AssignmentView) =>
    (!person || emailKey(v.row.personEmail) === person) &&
    (!q || [v.row.personName, v.row.label, v.row.customerKeys, v.row.note].join(" ").toLowerCase().includes(q));
  const listed = views
    .filter((v) => (view === "needs" ? listedInNeeds(v, linkedHere) : showRetired || v.row.retired !== "Yes"))
    .filter(kept);
  // Ask AI sets the view, the search and the person (`axai-admin.ts`); what each would list, counted as here.
  const listedFor = (a: AdminView) => {
    if (a.tab !== "assignments") return 0;
    const words = a.search.trim().toLowerCase();
    if (a.view === "no-owner") return noOwnerItems(unassignedCustomers(customers, all)).length;
    return views
      .filter((v) => (a.view === "needs" ? listedInNeeds(v, linkedHere) : showRetired || v.row.retired !== "Yes"))
      .filter(
        (v) =>
          (!a.person || emailKey(v.row.personEmail) === a.person) &&
          (!words ||
            [v.row.personName, v.row.label, v.row.customerKeys, v.row.note].join(" ").toLowerCase().includes(words)),
      ).length;
  };
  useAdminAskTab({
    view: { tab: "assignments", view, search, person },
    apply: (a) => {
      if (a.tab !== "assignments") return;
      if (a.view !== view) choose(a.view);
      setSearch(a.search);
      setPerson(a.person);
    },
    shownFor: listedFor,
    people: people.map(([email, name]) => ({ email, name })),
  });
  // The sheet's rows not here yet (same person, label and role), retired ones included: added once.
  const have = new Set(all.map((a) => `${emailKey(a.personEmail)}|${a.label.toLowerCase()}|${a.role}`));
  const toLoad = sheetRows().filter((r) => !have.has(`${r.personEmail}|${r.label.toLowerCase()}|${r.role}`));

  const loadSheet = async () => {
    const ok = await message.confirm(
      "Load the team's sheet?",
      `Adds ${countText(toLoad.length, "assignment")} with no customer codes yet. Then Needs confirming lists the codes each label may mean: link them one by one, or all at once.`,
      { confirmLabel: "Load" },
    );
    if (!ok) return;
    setLoading(true);
    let done = 0;
    try {
      for (const r of toLoad) {
        await w.add({ ...r, customerKeys: "", retired: "No" }, done ? undefined : personOnlyRow(all, r.personEmail));
        done++;
      }
      notify(`${countText(done, "assignment")} loaded`, "Confirm each one's customer codes under Needs confirming.");
    } catch (e) {
      await message.error("Couldn't load the sheet", crudErrorText(e), [
        `${countText(done, "assignment")} of ${toLoad.length} were added; Load from sheet adds the rest.`,
      ]);
    } finally {
      setLoading(false);
      await w.refresh();
    }
  };

  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  return (
    <div className="space-y-3">
      <div className="pt-2">
        <SegmentedViews
          label="Customer assignments views"
          value={view}
          onChange={choose}
          testId="assignments-view"
          segments={[
            { id: "needs", label: "Needs confirming", n: counts.needs, warn: true },
            { id: "assignments", label: "Assignments", n: counts.assignments },
            { id: "no-owner", label: "No owner", n: counts.noOwner },
          ]}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="border-input bg-background h-8 rounded-md border px-2 text-sm"
          aria-label="Person"
          value={person}
          onChange={(e) => setPerson(e.target.value)}
        >
          <option value="">Everyone</option>
          {people.map(([email, name]) => (
            <option key={email} value={email}>
              {name}
            </option>
          ))}
        </select>
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder={view === "no-owner" ? "Search — source, code, name" : "Search — person, customers, code"}
        />
        {view === "assignments" ? (
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
            Show retired
          </label>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <AddAssignment assignments={all} customers={customers} />
          {toLoad.length ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={loading}
              onClick={() => void loadSheet()}
              data-testid="load-sheet"
            >
              <UploadIcon className="size-3.5" />
              Load from sheet ({toLoad.length})
            </Button>
          ) : null}
        </div>
      </div>
      {view === "no-owner" ? (
        <NoOwnerCustomers
          customers={unassignedCustomers(customers, all)}
          people={people}
          assignments={all}
          pending={pending}
          search={search}
          person={person}
        />
      ) : (
        <>
          <PersonGroups
            // Each view keeps its own opened people.
            key={view}
            storage={{ who, view }}
            filtering={q || person ? `${q}|${person}` : ""}
            views={listed}
            everyView={views}
            assignments={all}
            customers={customers}
            byKey={byKey}
            holders={holders}
            suggestionsOpen={view === "needs"}
            linkedHere={linkedHere}
            onLinkedHere={(id, keys) =>
              setLinkedHere((m) => new Map(m).set(id, [...new Set([...(m.get(id) ?? []), ...keys])]))
            }
            held={held}
            onUnlinked={(id, keys, key) => setHeld((m) => new Map(m).set(id, holdUnlinked(m.get(id), keys, key)))}
            onRelinked={(id, key) =>
              setHeld((m) => {
                const was = m.get(id);
                return was ? new Map(m).set(id, { ...was, unlinked: was.unlinked.filter((k) => k !== key) }) : m;
              })
            }
            onUndone={(id) =>
              setLinkedHere((m) => {
                const next = new Map(m);
                next.delete(id);
                return next;
              })
            }
            empty={
              view === "needs"
                ? "Nothing needs confirming: every label has a code linked."
                : all.length
                  ? "No assignment matches."
                  : "No assignment yet: Add one, or Load from sheet."
            }
          />
          <footer className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
            <span>
              {countText(counts.assignments, "assignment")} · {countText(activePeople(all), "person", "people")}
            </span>
            <span className="ml-auto">Owner and Backup both open the Worklist and History on their customers</span>
          </footer>
        </>
      )}
    </div>
  );
}

/**
 * The assignments under one header per person (name, email, "11 customers · 3 not linked"), each closed
 * until opened — the opened ones kept per view in this browser; a search or a person chosen opens the
 * groups it keeps. Expand all · Collapse all above. A header has "Assign…" (Ana's customers to someone
 * else) and, for a person holding customers assigned from Ana, "Unassign Ana's customers".
 */
function PersonGroups({
  storage,
  filtering,
  views,
  everyView,
  assignments,
  customers,
  byKey,
  holders,
  suggestionsOpen,
  linkedHere,
  onLinkedHere,
  onUndone,
  held,
  onUnlinked,
  onRelinked,
  empty,
}: {
  storage: { who: string; view: AssignmentsView };
  /** The search and person filter, "" when neither: while on, the groups they keep are open. */
  filtering: string;
  views: readonly AssignmentView[];
  /** Every assignment's: a person's header counts all theirs, not only those the filters keep. */
  everyView: readonly AssignmentView[];
  assignments: readonly CustomerAssignmentRecord[];
  customers: readonly CustomerOption[];
  byKey: ReadonlyMap<string, CustomerOption>;
  holders: ReadonlyMap<string, readonly CodeHolder[]>;
  suggestionsOpen: boolean;
  /** The rows linked in this visit (kept in place, "Linked ✓ · Undo"). */
  linkedHere: ReadonlyMap<string, readonly string[]>;
  onLinkedHere: (id: string, keys: readonly string[]) => void;
  onUndone: (id: string) => void;
  /** The rows' chips held after an unlink in this visit (struck through, in place). */
  held: ReadonlyMap<string, HeldChips>;
  onUnlinked: (id: string, keys: readonly string[], key: string) => void;
  onRelinked: (id: string, key: string) => void;
  empty: string;
}) {
  const w = useAssignmentWrites();
  const message = useMessage();
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => readOpened(storage.who, storage.view));
  // Closed by hand while a search or person filter is on: forgotten when it changes.
  const [closedNow, setClosedNow] = useState<{ filter: string; emails: ReadonlySet<string> }>({
    filter: "",
    emails: new Set(),
  });
  const [cover, setCover] = useState<{ email: string; name: string } | null>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const closedWhileFiltering = closedNow.filter === filtering ? closedNow.emails : new Set<string>();
  const byPerson = new Map<string, AssignmentView[]>();
  for (const v of views)
    byPerson.set(emailKey(v.row.personEmail), [...(byPerson.get(emailKey(v.row.personEmail)) ?? []), v]);
  const groups = [...byPerson]
    .map(([email, rows]) => ({
      email,
      name: rows.find((v) => !v.person)?.row.personName ?? rows[0]!.row.personName,
      rows: rows.filter((v) => !v.person).sort((a, b) => a.row.label.localeCompare(b.row.label)),
      personOnly: rows.find((v) => v.person && v.row.retired !== "Yes")?.row,
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.email.localeCompare(b.email));
  const isOpen = (email: string) => personGroupOpen(email, { opened, filtering: !!filtering, closedWhileFiltering });
  const keepOpened = (next: ReadonlySet<string>) => {
    setOpened(next);
    writeOpened(storage.who, storage.view, next);
  };
  const toggle = (email: string, open: boolean) => {
    if (filtering) {
      const emails = new Set(closedWhileFiltering);
      if (open) emails.delete(email);
      else emails.add(email);
      setClosedNow({ filter: filtering, emails });
      return;
    }
    const next = new Set(opened);
    if (open) next.add(email);
    else next.delete(email);
    keepOpened(next);
  };
  const setAll = (open: boolean) => {
    const emails = groups.map((g) => g.email);
    if (filtering) setClosedNow({ filter: filtering, emails: new Set(open ? [] : emails) });
    else keepOpened(open ? new Set([...opened, ...emails]) : new Set());
  };
  /** "Unassign Ana's customers": the rows assigned from her retired, one save each, asked first. */
  const endCover = async (
    to: { email: string; name: string },
    held: { name: string; rows: CustomerAssignmentRecord[] },
  ) => {
    const p = endCoverPrompt(to.name, held.name, held.rows.length);
    if (!(await message.confirm(p.title, p.text, { confirmLabel: "Unassign" }))) return;
    setEnding(`${to.email}|${held.name}`);
    try {
      const r = await saveEach(
        held.rows,
        (row) => w.put(row, { retired: "Yes" }, unassignNote(held.name)),
        crudErrorText,
      );
      if (r.failed.length)
        await message.error(
          `Unassigned ${r.done.length} · ${r.failed.length} failed`,
          "The failed ones are still assigned: Unassign tries them again.",
          r.failed.map((f) => `${f.item.label} — ${f.why}`),
        );
      else
        notify(
          `${held.name}'s customers unassigned from ${to.name}`,
          `${countText(r.done.length, "assignment")} retired.`,
        );
    } finally {
      setEnding(null);
      await w.refresh();
    }
  };
  return (
    <div className="space-y-1.5">
      {groups.length ? (
        <div className="flex items-center gap-1 text-xs">
          <Button size="xs" variant="ghost" onClick={() => setAll(true)} data-testid="assignments-expand-all">
            Expand all
          </Button>
          <span className="text-muted-foreground">·</span>
          <Button size="xs" variant="ghost" onClick={() => setAll(false)} data-testid="assignments-collapse-all">
            Collapse all
          </Button>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-md border" data-testid="admin-assignments">
        <table className="w-full text-[13px]">
          <thead className="bg-locked text-left text-xs">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
              <th className="w-48">Customer</th>
              <th>Linked codes</th>
              <th className="w-24">Role</th>
              <th className="w-40">Note</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted-foreground p-6">
                  {empty}
                </td>
              </tr>
            ) : null}
            {groups.map((g) => {
              const open = isOpen(g.email);
              const theirs = everyView.filter((v) => emailKey(v.row.personEmail) === g.email);
              const coverable = coverPlan(assignments, g.email, "").length;
              const covers = coversHeld(assignments, g.email);
              return (
                <Fragment key={g.email}>
                  <tr className="bg-muted/40 border-t">
                    <td colSpan={5} className="px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-left"
                          aria-expanded={open}
                          onClick={() => toggle(g.email, !open)}
                          data-testid="assignments-person"
                        >
                          {open ? <ChevronDownIcon className="size-3.5" /> : <ChevronRightIcon className="size-3.5" />}
                          <span className="font-semibold">{g.name}</span>
                          <span className="text-muted-foreground text-xs">{g.email}</span>
                          <span className="text-muted-foreground text-xs">· {personSummary(theirs)}</span>
                        </button>
                        <span className="ml-auto flex flex-wrap items-center gap-1">
                          {covers.map((h) => (
                            <Button
                              key={h.name}
                              size="xs"
                              variant="outline"
                              disabled={ending !== null}
                              title={`Retire the ${countText(h.rows.length, "assignment")} ${g.name} holds, assigned from ${h.name}`}
                              onClick={() => void endCover(g, h)}
                              data-testid="end-cover"
                            >
                              {ending === `${g.email}|${h.name}` ? "Unassigning…" : `Unassign ${h.name}'s customers`}
                            </Button>
                          ))}
                          {coverable ? (
                            <Button
                              size="xs"
                              variant="outline"
                              title={`Assign ${g.name}'s customers to someone else, as Backup (while ${g.name} is away)`}
                              onClick={() => setCover({ email: g.email, name: g.name })}
                              data-testid="cover-for"
                            >
                              Assign…
                            </Button>
                          ) : null}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {open && !g.rows.length && g.personOnly ? (
                    <PersonOnlyLine row={g.personOnly} customers={customers} holders={holders} />
                  ) : null}
                  {open
                    ? g.rows.map((v) => (
                        <AssignmentLine
                          key={v.row.id}
                          view={v}
                          customers={customers}
                          byKey={byKey}
                          holders={holders}
                          suggestionsOpen={suggestionsOpen}
                          linkedHere={linkedHere.get(v.row.id)}
                          onLinked={(keys) => onLinkedHere(v.row.id, keys)}
                          onUndone={() => onUndone(v.row.id)}
                          held={held.get(v.row.id)}
                          onUnlinked={(key) => onUnlinked(v.row.id, v.keys, key)}
                          onRelinked={(key) => onRelinked(v.row.id, key)}
                        />
                      ))
                    : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {cover ? <CoverDialog from={cover} assignments={assignments} onClose={() => setCover(null)} /> : null}
    </div>
  );
}

/**
 * A person added with no customer yet: "No customers yet", with "+ Link" — the codes ticked fill their
 * record, the label the first one's shown name — and Remove (retired, as any assignment).
 */
function PersonOnlyLine({
  row,
  customers,
  holders,
}: {
  row: CustomerAssignmentRecord;
  customers: readonly CustomerOption[];
  holders: ReadonlyMap<string, readonly CodeHolder[]>;
}) {
  const w = useAssignmentWrites();
  const message = useMessage();
  const [busy, setBusy] = useState<string | null>(null);
  const save = async (patch: Partial<Input>, note: string) => {
    setBusy("Saving…");
    try {
      await w.update(row, patch, note);
    } finally {
      setBusy(null);
    }
  };
  return (
    <tr className={cn("border-t [&>td]:px-3 [&>td]:py-1.5", busy && "opacity-60")} data-testid="person-only">
      <td colSpan={2}>
        <span className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
          No customers yet —
          <LinkPicker
            customers={customers}
            row={row}
            linked={[]}
            suggestions={[]}
            holders={holders}
            disabled={!!busy}
            onLink={async (picks) => {
              const keys = codesOfPicks(picks).map((c) => c.key);
              setBusy("Saving…");
              try {
                await w.put(row, { label: picks[0]!.label, customerKeys: joinKeys(keys) }, `Linked ${keys.join(", ")}`);
                notify(`Linked ${countText(keys.length, "code")} to ${row.personName}`, `As “${picks[0]!.label}”.`);
                return [];
              } catch (e) {
                await message.error("Couldn't link the customers", crudErrorText(e), [
                  "They are still ticked: Link tries again.",
                ]);
                return keys;
              } finally {
                setBusy(null);
                await w.refresh();
              }
            }}
          />
          <span>· or add them from No owner</span>
        </span>
      </td>
      <td>{row.role}</td>
      <td />
      <td>
        <Button
          size="xs"
          variant="outline"
          disabled={!!busy}
          title="Take them off the page (retired: Show retired brings them back)"
          onClick={() => void save({ retired: "Yes" }, "Retired")}
        >
          Remove
        </Button>
      </td>
    </tr>
  );
}

/**
 * Assign Ana's customers: her active assignments assigned to the person chosen, as Backup — the same
 * label and codes, the note "Assigned from Ana" — one save and one System log entry each. Those the
 * person has already (all its codes on one row) are skipped and said; failed ones are listed with Retry.
 */
function CoverDialog({
  from,
  assignments,
  onClose,
}: {
  from: { email: string; name: string };
  assignments: readonly CustomerAssignmentRecord[];
  onClose: () => void;
}) {
  const w = useAssignmentWrites();
  const drag = useDialogDrag();
  const [email, setEmail] = useState("");
  const [name, setName] = useState({ value: "", byHand: false });
  const [note, setNote] = useState(coverNote(from.name));
  // Ticks changed by hand; else an item is ticked when the dialog opens as `coverTickedAtFirst` says.
  const [tickedByHand, setTickedByHand] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [busy, setBusy] = useState(false);
  // After a run with failures: what it did, and the failed ones (Retry saves only those).
  const [result, setResult] = useState<{
    done: number;
    skipped: number;
    failed: { item: CoverItem; why: string }[];
  } | null>(null);
  // Someone on the page already keeps the name it shows them by (Assign… never renames them).
  const to = { email: emailKey(email), name: (pageNameOf(assignments, email) ?? name.value).trim() };
  const chosen = isEmail(to.email) && !!to.name && to.email !== emailKey(from.email);
  const plan = coverPlan(assignments, from.email, chosen ? to.email : "");
  const toCopy = result
    ? result.failed.map((f) => f.item)
    : plan.filter((i) => !i.skip && (tickedByHand.get(i.row.id) ?? coverTickedAtFirst(i)));
  const skipped = plan.filter((i) => i.skip).length;
  const copy = async () => {
    setBusy(true);
    try {
      let into = personOnlyRow(assignments, to.email);
      const r = await saveEach(
        toCopy,
        async (item) => {
          await w.add(coverInput(item, to, note), into);
          into = undefined;
        },
        crudErrorText,
      );
      const done = (result?.done ?? 0) + r.done.length;
      const skippedNow = result?.skipped ?? skipped;
      const text = coverResultText({
        to: to.name,
        from: from.name,
        done,
        skipped: skippedNow,
        failed: r.failed.length,
      });
      if (r.failed.length) setResult({ done, skipped: skippedNow, failed: r.failed });
      else {
        notify(
          text,
          `Their rows say “${note.trim()}”: Unassign ${from.name}'s customers on ${to.name}'s header retires them.`,
        );
        onClose();
      }
    } finally {
      setBusy(false);
      await w.refresh();
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        initialFocus={drag.ref}
        className="sm:max-w-lg"
        data-testid="cover-dialog"
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>
            {chosen
              ? `Assign ${countText(toCopy.length, "customer")} to ${to.name}`
              : `Assign ${from.name}'s customers`}
          </DialogTitle>
          <DialogDescription>
            Assigns {from.name}'s customers to someone else as Backup, while {from.name} is away. Backup opens the
            Worklist and History on those customers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-[13px]">
          <label className="block text-xs font-medium">Assign to</label>
          <PersonPicker
            enabled
            exclude={from.email}
            email={email}
            onEmail={(typed, known) => {
              setEmail(typed);
              setName((n) => nameAfterEmail(n, known));
              setResult(null);
            }}
            onPick={(p) => {
              setEmail(p.email);
              setName({ value: p.name, byHand: false });
              setResult(null);
            }}
          />
          {isEmail(to.email) && (name.byHand || !name.value.trim()) ? (
            <Input
              className="h-8"
              placeholder="Name"
              aria-label="Name"
              value={name.value}
              onChange={(e) => setName({ value: e.target.value, byHand: e.target.value.trim() !== "" })}
            />
          ) : null}
          <div className="text-xs">
            <span className="font-medium">{from.name}'s assignments</span>{" "}
            <span className="text-muted-foreground">— assigned as Backup</span>
          </div>
          <ul className="max-h-64 space-y-0.5 overflow-y-auto rounded-md border p-1.5" data-testid="cover-items">
            {plan.map((i) => (
              <li
                key={i.row.id}
                className={cn("flex items-center gap-1.5", (i.skip || i.empty) && "text-muted-foreground")}
              >
                <input
                  type="checkbox"
                  aria-label={`Assign ${i.row.label}`}
                  checked={!i.skip && (tickedByHand.get(i.row.id) ?? coverTickedAtFirst(i))}
                  disabled={!!i.skip || busy || !!result}
                  onChange={(e) => setTickedByHand((t) => new Map(t).set(i.row.id, e.target.checked))}
                />
                <span className="truncate">{i.row.label}</span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {i.keys.length ? countText(i.keys.length, "code") : "no code linked"} · {i.row.role}
                </span>
                {coverItemNote(i, to.name) ? (
                  <span className="ml-auto shrink-0 text-xs">{coverItemNote(i, to.name)}</span>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium">Assign as:</span> Backup
          </div>
          <label className="block space-y-1 text-xs">
            <span className="font-medium">Note</span>
            <Input
              className="h-8 text-[13px]"
              maxLength={200}
              value={note}
              disabled={busy || !!result}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          {result ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs" data-testid="cover-result">
              <p className="font-medium">
                {coverResultText({
                  to: to.name,
                  from: from.name,
                  done: result.done,
                  skipped: result.skipped,
                  failed: result.failed.length,
                })}
              </p>
              <ul className="mt-1 list-disc pl-4">
                {result.failed.map((f) => (
                  <li key={f.item.row.id}>
                    {f.item.row.label} — {f.why}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {result ? "Close" : "Cancel"}
          </Button>
          <Button disabled={busy || !chosen || !toCopy.length} onClick={() => void copy()} data-testid="cover-copy">
            {busy ? "Assigning…" : result ? `Retry ${result.failed.length}` : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * No owner: the open lines' customers no Owner has, biggest first (or by name), customers sharing a
 * shown name as one item — ticking it ticks all its codes. Ticked ones are added to a person at once:
 * to one of their assignments, or a new one (its label typed, Owner unless said). One save, the
 * assignment's own System log entry, as a Link is. A customer suggested for someone's label says so,
 * with a button that links it there.
 */
function NoOwnerCustomers({
  customers,
  people,
  assignments,
  pending,
  search,
  person,
}: {
  customers: readonly CustomerOption[];
  people: readonly [string, string][];
  assignments: readonly CustomerAssignmentRecord[];
  pending: ReadonlyMap<string, readonly PendingSuggestion[]>;
  search: string;
  /** The person filter: the bar adds to them until another is chosen there. */
  person: string;
}) {
  const w = useAssignmentWrites();
  const message = useMessage();
  const [sort, setSort] = useState<"lines" | "name">("lines");
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [chosenEmail, setEmail] = useState<string | null>(null);
  /** The assignment to add to: its id, or "" for a new one. */
  const [target, setTarget] = useState("");
  // The new assignment's label: typed, else the first ticked item's shown name (`noOwnerLabel`).
  const [typedLabel, setLabel] = useState<string | null>(null);
  const [role, setRole] = useState<Input["role"]>("Owner");
  const [busy, setBusy] = useState<string | null>(null);
  const email = chosenEmail ?? person;
  const items = noOwnerItems(customers, sort);
  const label = typedLabel ?? noOwnerLabel(items, ticked);
  const shown = items.filter(
    (i) =>
      !search.trim() ||
      (i.shown ?? "").toLowerCase().includes(search.trim().toLowerCase()) ||
      i.codes.some((c) => customerMatches(c, search)),
  );
  // Only what is still listed: a customer that got an Owner meanwhile leaves the selection.
  const keys = keysOfItems(items, ticked);
  const allShown = shown.length > 0 && shown.every((i) => ticked.has(i.id));
  const theirs = assignments
    .filter((a) => a.retired !== "Yes" && !isPersonOnly(a) && emailKey(a.personEmail) === email)
    .sort((a, b) => a.label.localeCompare(b.label));
  const into = theirs.find((a) => a.id === target);
  const name = people.find(([e]) => e === email)?.[1] ?? "";
  const tick = (ids: readonly string[], on: boolean) =>
    setTicked((t) => {
      const next = new Set(t);
      for (const k of ids) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  const add = async () => {
    const as = into?.role ?? role;
    setBusy("Adding…");
    try {
      if (into) {
        const ok = await w.update(
          into,
          (now) => ({ customerKeys: linkKeys(now.customerKeys, keys) }),
          `Linked ${keys.join(", ")}`,
        );
        if (!ok) return;
      } else {
        await w.add(
          {
            personEmail: email,
            personName: name,
            label: label.trim(),
            customerKeys: joinKeys(keys),
            role,
            note: "",
            retired: "No",
          },
          personOnlyRow(assignments, email),
        );
        await w.refresh();
      }
      notify(
        `Added ${countText(keys.length, "customer")} to ${name} as ${as}`,
        into ? `On “${into.label}”.` : `A new assignment, “${label.trim()}”.`,
      );
      setTicked(new Set());
      setLabel(null);
    } catch (e) {
      // One save: none was added, every one stays ticked.
      await message.error("Couldn't add the customers", crudErrorText(e), [`Not added: ${keys.join(", ")}`]);
      await w.refresh();
    } finally {
      setBusy(null);
    }
  };
  /** "Link to Mirihan": the item's codes suggested for that assignment, linked there (one save). */
  const linkTo = async (s: PendingSuggestion & { keys: string[] }) => {
    const row = assignments.find((a) => a.id === s.id);
    if (!row) return;
    setBusy(`Linking to ${s.personName}…`);
    try {
      const ok = await w.update(
        row,
        (now) => ({ customerKeys: linkKeys(now.customerKeys, s.keys) }),
        `Linked ${s.keys.join(", ")}`,
      );
      if (ok) notify(`Linked ${countText(s.keys.length, "code")} to ${s.label} (${s.personName})`);
    } finally {
      setBusy(null);
    }
  };
  const ready = keys.length > 0 && !!email && (!!into || !!label.trim());
  return (
    <section className="space-y-2" data-testid="no-owner">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">
          Customers with open lines and no owner{" "}
          <span className="text-muted-foreground font-normal">({customers.length})</span>
        </h3>
        {customers.length ? (
          <>
            <select
              className="border-input bg-background h-7 rounded-md border px-2 text-xs"
              aria-label="Sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as "lines" | "name")}
            >
              <option value="lines">Most open lines first</option>
              <option value="name">Name</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={allShown}
                disabled={!shown.length}
                onChange={(e) =>
                  tick(
                    shown.map((i) => i.id),
                    e.target.checked,
                  )
                }
              />
              {search.trim() ? `Select the ${shown.length} shown` : "Select all"}
            </label>
          </>
        ) : null}
      </div>

      <SelectionBar
        active={keys.length > 0}
        hint="Tick customers to add them to a person at once"
        // Nothing to tick: no bar (that never changes on a tick).
        className={customers.length ? undefined : "hidden"}
        data-testid="no-owner-add"
      >
        <span className="font-medium">{countText(keys.length, "customer")} selected</span>
        <span className="text-muted-foreground">→ add to</span>
        <select
          className="border-input bg-background h-8 rounded-md border px-2"
          aria-label="Person to add them to"
          value={email}
          disabled={!!busy}
          onChange={(e) => (setEmail(e.target.value), setTarget(""))}
        >
          <option value="">Choose a person…</option>
          {people.map(([e, n]) => (
            <option key={e} value={e}>
              {n}
            </option>
          ))}
        </select>
        {email ? (
          <select
            className="border-input bg-background h-8 rounded-md border px-2"
            aria-label="Assignment to add them to"
            value={target}
            disabled={!!busy}
            onChange={(e) => setTarget(e.target.value)}
          >
            <option value="">A new assignment…</option>
            {theirs.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label} ({a.role})
              </option>
            ))}
          </select>
        ) : null}
        {email && !into ? (
          <Input
            className="h-8 w-56"
            aria-label="Customers, as the sheet says them"
            placeholder="Customers, as the sheet says them"
            value={label}
            disabled={!!busy}
            onChange={(e) => setLabel(e.target.value)}
          />
        ) : null}
        <span className="text-muted-foreground">· Role</span>
        {into ? (
          <span title="The role of the assignment they are added to">{into.role}</span>
        ) : (
          <select
            className="border-input bg-background h-8 rounded-md border px-2"
            aria-label="Role"
            value={role}
            disabled={!!busy}
            onChange={(e) => setRole(e.target.value as Input["role"])}
          >
            <option>Owner</option>
            <option>Backup</option>
          </select>
        )}
        <Button size="sm" disabled={!!busy || !ready} onClick={() => void add()} data-testid="no-owner-add-button">
          {busy ?? `Add${name ? ` to ${name}` : ""}`}
        </Button>
        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setTicked(new Set())}>
          Clear
        </Button>
      </SelectionBar>
      {!customers.length ? (
        <p className="text-muted-foreground text-xs">Every customer with open lines has an Owner.</p>
      ) : shown.length ? (
        <ul className="grid gap-x-6 gap-y-0.5 text-[13px] lg:grid-cols-2">
          {shown.map((i) => (
            <NoOwnerLine
              key={i.id}
              item={i}
              ticked={ticked.has(i.id)}
              busy={!!busy}
              suggestions={itemSuggestions(i, pending)}
              onTick={(on) => tick([i.id], on)}
              onLink={(s) => void linkTo(s)}
            />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">No customer matches.</p>
      )}
    </section>
  );
}

/** One item of No owner: a customer, or a shown name over several codes (its codes on hover). */
function NoOwnerLine({
  item: i,
  ticked,
  busy,
  suggestions,
  onTick,
  onLink,
}: {
  item: NoOwnerItem;
  ticked: boolean;
  busy: boolean;
  suggestions: (PendingSuggestion & { keys: string[] })[];
  onTick: (on: boolean) => void;
  onLink: (s: PendingSuggestion & { keys: string[] }) => void;
}) {
  const c = i.codes[0]!;
  const first = suggestions[0];
  return (
    <li className="hover:bg-muted flex items-center gap-1.5 rounded px-1" data-testid="no-owner-item">
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          aria-label={`Select ${i.shown ?? `${c.code} ${c.name}`}`}
          checked={ticked}
          onChange={(e) => onTick(e.target.checked)}
        />
        {i.shown ? (
          <span
            className="flex min-w-0 items-center gap-1.5"
            title={i.codes
              .map((x) => `${x.source} ${x.code} · ${x.name} · ${countText(x.openLines, "line")}`)
              .join("\n")}
          >
            <span className="truncate font-medium">{i.shown}</span>
            <span className="text-muted-foreground shrink-0 text-xs">{countText(i.codes.length, "code")}</span>
          </span>
        ) : (
          <>
            <SourceBadge source={c.source} />
            <span className="font-mono text-xs">{c.code}</span>
            <span className="truncate">{optionName(c)}</span>
          </>
        )}
      </label>
      {first ? (
        <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
          <span title={suggestions.map((s) => `${s.personName}: ${s.label} (${s.role})`).join("\n")}>
            {suggestedForText(suggestions)}
          </span>
          <Button
            size="xs"
            variant="outline"
            disabled={busy}
            title={`Link ${first.keys.join(", ")} to ${first.label} for ${first.personName}`}
            onClick={() => onLink(first)}
          >
            Link to {first.personName}
          </Button>
        </span>
      ) : null}
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{countText(i.lines, "line")}</span>
    </li>
  );
}

/** " · 3 open lines", " · no open lines", " · no open lines, inactive". */
const openText = (c: CustomerOption) =>
  c.openLines ? ` · ${countText(c.openLines, "open line")}` : ` · no open lines${c.active ? "" : ", inactive"}`;

/** A linked code, solid: the customer's name, its code and source on hover; "also Kai (Backup)" when someone else links it. */
function CodeChip({
  k,
  byKey,
  others,
  onRemove,
}: {
  k: string;
  byKey: ReadonlyMap<string, CustomerOption>;
  others: readonly CodeHolder[];
  onRemove?: () => void;
}) {
  const c = byKey.get(k);
  const code = k.slice(k.indexOf(":") + 1);
  const source = c?.source ?? sourceOfCustomerKey(k);
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <span
        className="bg-muted inline-flex max-w-full items-center gap-1 rounded border py-0.5 pr-0.5 pl-1.5 text-xs"
        title={`${k}${c ? ` — ${c.name}${openText(c)}` : " — not in A2000's or Deposco's customers now"}`}
        data-testid="linked-code"
      >
        {source ? <SourceBadge source={source} /> : null}
        <span className="font-mono">{code}</span>
        {c?.name ? <span className="text-muted-foreground truncate">{optionName(c)}</span> : null}
        {onRemove ? (
          <button
            type="button"
            aria-label={`Unlink ${code}`}
            className="hover:bg-background rounded p-0.5"
            onClick={onRemove}
          >
            <XIcon className="size-3" />
          </button>
        ) : null}
      </span>
      {others.length ? (
        <span className="text-muted-foreground text-[11px]" title={holdersHover(others)}>
          {alsoLinkedText(others)}
        </span>
      ) : null}
    </span>
  );
}

/** A code unlinked in this visit, in its place: "AEROAG · unlinked · Undo" — nothing slides under the pointer. */
function UnlinkedChip({ k, disabled, onUndo }: { k: string; disabled: boolean; onUndo: () => void }) {
  const code = k.slice(k.indexOf(":") + 1);
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-1 rounded border border-dashed py-0.5 pr-0.5 pl-1.5 text-xs"
      data-testid="unlinked-code"
    >
      <span className="font-mono line-through">{code}</span>
      <span>· unlinked ·</span>
      <button
        type="button"
        className="text-primary hover:bg-muted rounded px-1 font-medium"
        disabled={disabled}
        title={`Link ${code} again`}
        onClick={onUndo}
      >
        Undo
      </button>
    </span>
  );
}

/**
 * A suggested code, outlined: its tick (Tab reaches it, Space ticks; a click on the chip's body too) for
 * Link N selected; "+ Link" links it alone at once; "linked for Kai" when someone else has it.
 */
function SuggestionChip({
  c,
  row,
  others,
  disabled,
  ticked,
  onTick,
  onLink,
}: {
  c: CustomerOption;
  row: CustomerAssignmentRecord;
  others: readonly CodeHolder[];
  disabled: boolean;
  ticked: boolean;
  onTick: (on: boolean) => void;
  onLink: () => void;
}) {
  return (
    <span
      className={cn(
        "inline-flex cursor-pointer items-center gap-1 rounded border border-dashed border-sky-400 py-0.5 pr-0.5 pl-1 text-xs",
        ticked ? "bg-sky-50" : "bg-white",
      )}
      title={`${suggestionHover(row)} — ${c.key}, ${c.name}${openText(c)}${others.length ? `\n${holdersHover(others)}` : ""}`}
      data-testid="suggested-code"
      onClick={(e) => {
        if (!disabled && !(e.target as HTMLElement).closest("button, input")) onTick(!ticked);
      }}
    >
      <input
        type="checkbox"
        aria-label={`Select ${c.code}`}
        checked={ticked}
        disabled={disabled}
        onChange={(e) => onTick(e.target.checked)}
      />
      <SourceBadge source={c.source} />
      <span className="font-mono">{c.code}</span>
      <span className="text-muted-foreground">{optionName(c)}</span>
      {others.length ? <span className="text-[11px] text-amber-700">{linkedForText(others)}</span> : null}
      <button
        type="button"
        className="text-primary hover:bg-muted inline-flex items-center gap-0.5 rounded px-1 font-medium"
        disabled={disabled}
        onClick={onLink}
      >
        <PlusIcon className="size-3" />
        Link
      </button>
    </span>
  );
}

function AssignmentLine({
  view: v,
  customers,
  byKey,
  holders,
  suggestionsOpen,
  linkedHere,
  onLinked,
  onUndone,
  held,
  onUnlinked,
  onRelinked,
}: {
  view: AssignmentView;
  customers: readonly CustomerOption[];
  byKey: ReadonlyMap<string, CustomerOption>;
  holders: ReadonlyMap<string, readonly CodeHolder[]>;
  suggestionsOpen: boolean;
  /** The codes linked on this row in this visit: "Linked ✓ 1 code · Undo" where its suggestions were. */
  linkedHere?: readonly string[];
  onLinked: (keys: readonly string[]) => void;
  onUndone: () => void;
  /** Its chips as an unlink in this visit left them: an unlinked one stays in place, struck through. */
  held?: HeldChips;
  onUnlinked: (key: string) => void;
  onRelinked: (key: string) => void;
}) {
  const row = v.row;
  const w = useAssignmentWrites();
  const message = useMessage();
  /** What runs, said on the row ("Saving…", "Linking 7…"); the row's buttons are off meanwhile. */
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [editRole, setEditRole] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(suggestionsOpen);
  // The suggestions ticked (Link N selected): until they are linked, Hide, or the view changes.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const keys = v.keys;
  const retired = row.retired === "Yes";
  const suggestions = v.suggestions;
  const save = async (patch: Patch, note = "") => {
    setBusy("Saving…");
    try {
      await w.update(row, patch, note);
    } finally {
      setBusy(null);
    }
  };
  // Retire asks first: its customers leave the person's My customers. Restore (Show retired) undoes it.
  const retireOrRestore = async () => {
    if (retired) return save({ retired: "No" }, "Restored");
    const ok = await message.confirm(
      `Retire “${row.label}” for ${row.personName}?`,
      "Its customers leave their My customers. It stays here, under Show retired, and can be restored.",
      { confirmLabel: "Retire" },
    );
    if (ok) await save({ retired: "Yes" }, "Retired");
  };
  const link = async (k: string) => {
    setBusy("Saving…");
    try {
      if (await w.update(row, (now) => ({ customerKeys: linkKeys(now.customerKeys, [k]) }), `Linked ${k}`))
        onLinked([k]);
    } finally {
      setBusy(null);
    }
  };
  /** An unlink's Undo (the toast's or the struck chip's): the code linked again, its chip back in place. */
  const relink = async (k: string) => {
    if (await w.update(row, (now) => ({ customerKeys: linkKeys(now.customerKeys, [k]) }), `Linked ${k} again (Undo)`))
      onRelinked(k);
  };
  // One click: the chip stays where it was, struck through with its Undo, and a toast with Undo too.
  const unlink = async (k: string) => {
    setBusy("Saving…");
    try {
      if (await w.update(row, (now) => ({ customerKeys: unlinkKeys(now.customerKeys, [k]) }), `Unlinked ${k}`)) {
        onUnlinked(k);
        notifyUndo(unlinkedText(k, row), () => void relink(k));
      }
    } finally {
      setBusy(null);
    }
  };
  // Link all's (or a Link's) Undo, from Needs confirming: those codes unlinked again, the row back to Not linked yet.
  const undoLinked = async () => {
    if (!linkedHere?.length) return;
    setBusy("Undoing…");
    try {
      if (
        await w.update(
          row,
          (now) => ({ customerKeys: unlinkKeys(now.customerKeys, linkedHere) }),
          `Unlinked ${linkedHere.join(", ")} (Undo)`,
        )
      )
        onUndone();
    } finally {
      setBusy(null);
    }
  };
  /**
   * Links `codes`, one save and one change entry each (`linkEach`, as Link all always did); codes linked
   * for someone else are named first. Answers the keys not saved, or null when the person went back.
   */
  const linkCodes = async (
    codes: readonly CustomerOption[],
    confirmLabel: string,
  ): Promise<readonly string[] | null> => {
    const shared = sharedBeforeLinkAll(codes, holders, row);
    if (
      shared &&
      !(await message.confirm(
        `Link ${countText(codes.length, "code")} to ${row.label} for ${row.personName}?`,
        shared,
        {
          confirmLabel,
        },
      ))
    )
      return null;
    setBusy(`Linking ${codes.length}…`);
    try {
      const r = await linkEach(
        codes.map((c) => c.key),
        (k) => w.link(row, k),
        crudErrorText,
      );
      if (r.linked.length) onLinked(r.linked);
      if (r.failed.length)
        await message.error(
          linkAllText(r, row),
          "The failed ones stay ticked: try again.",
          r.failed.map((f) => `${f.key} — ${f.why}`),
        );
      else notify(linkAllText(r, row));
      return r.failed.map((f) => f.key);
    } finally {
      setBusy(null);
      await w.refresh();
    }
  };
  const linkAll = () => linkCodes(suggestions, "Link all");
  /** "Link 3 selected": the ticked suggestions only; the failed ones stay ticked, the rest stay suggested. */
  const linkSelected = async () => {
    const failed = await linkCodes(tickedSuggestions(suggestions, picked), `Link ${picked.size} selected`);
    if (failed) setPicked(new Set(failed));
  };
  const off = !!busy;
  const chips = chipsByPlace(keys, held, linkedHere);
  const chip = ({ key: k, unlinked }: { key: string; unlinked: boolean }) =>
    unlinked ? (
      <UnlinkedChip key={k} k={k} disabled={off} onUndo={() => void relink(k)} />
    ) : (
      <CodeChip
        key={k}
        k={k}
        byKey={byKey}
        others={otherHolders(holders, k, row)}
        onRemove={retired || off ? undefined : () => void unlink(k)}
      />
    );
  return (
    <tr className={cn("group border-t align-top [&>td]:px-3 [&>td]:py-1.5", (off || retired) && "opacity-60")}>
      <td>
        <span className="font-medium">{row.label}</span>{" "}
        {!keys.length && !retired ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap text-amber-800">
            Not linked yet
          </span>
        ) : null}
        {retired ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">Retired</span>
        ) : null}
        {busy ? <span className="text-muted-foreground block text-xs">{busy}</span> : null}
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          {chips.top.map(chip)}
          {v.needs && !suggestions.length ? (
            <span className="text-muted-foreground text-xs">No suggestions —</span>
          ) : null}
          {retired ? null : (
            <LinkPicker
              customers={customers}
              row={row}
              linked={keys}
              suggestions={suggestions}
              holders={holders}
              disabled={off}
              onLink={(picks) => linkCodes(codesOfPicks(picks), "Link")}
            />
          )}
        </div>
        {linkedHere?.length ? (
          <div className="mt-1 space-y-1" data-testid="linked-here">
            {/* Where the "Suggested codes" line was; the codes just linked grow below it, nothing above moves. */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-medium text-emerald-700">{linkedHereText(linkedHere)}</span>
              <span className="text-muted-foreground">·</span>
              <Button
                size="xs"
                variant="ghost"
                disabled={off}
                title={`Unlink ${linkedHere.join(", ")} again`}
                onClick={() => void undoLinked()}
                data-testid="undo-link"
              >
                Undo
              </Button>
            </div>
            {chips.fresh.length ? (
              <div className="flex flex-wrap items-center gap-1">{chips.fresh.map(chip)}</div>
            ) : null}
          </div>
        ) : null}
        {suggestions.length ? (
          <div className="mt-1 space-y-1" data-testid="suggestions">
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Suggested codes ({suggestions.length}) —</span>
              {picked.size ? (
                <Button size="xs" disabled={off} onClick={() => void linkSelected()} data-testid="link-selected">
                  Link {picked.size} selected
                </Button>
              ) : null}
              <Button size="xs" variant="outline" disabled={off} onClick={() => void linkAll()} data-testid="link-all">
                Link all
              </Button>
              {showSuggestions ? (
                <>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={picked.size === suggestions.length}
                    onClick={() => setPicked(new Set(suggestions.map((c) => c.key)))}
                  >
                    Select all
                  </Button>
                  <Button size="xs" variant="ghost" disabled={!picked.size} onClick={() => setPicked(new Set())}>
                    None
                  </Button>
                </>
              ) : null}
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setShowSuggestions((s) => !s);
                  setPicked(new Set());
                }}
              >
                {showSuggestions ? "Hide" : "Show"}
              </Button>
            </div>
            {showSuggestions ? (
              <div className="flex flex-wrap items-center gap-1">
                {suggestions.map((c) => (
                  <SuggestionChip
                    key={c.key}
                    c={c}
                    row={row}
                    others={otherHolders(holders, c.key, row)}
                    disabled={off}
                    ticked={picked.has(c.key)}
                    onTick={(on) =>
                      setPicked((p) => {
                        const next = new Set(p);
                        if (on) next.add(c.key);
                        else next.delete(c.key);
                        return next;
                      })
                    }
                    onLink={() => void link(c.key)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </td>
      <td>
        {editRole ? (
          <select
            className="border-input bg-background h-7 rounded-md border px-1.5 text-[13px]"
            aria-label={`Role of ${row.personName} for ${row.label}`}
            value={row.role}
            disabled={off}
            autoFocus
            onBlur={() => setEditRole(false)}
            onChange={(e) => {
              setEditRole(false);
              void save({ role: e.target.value as Input["role"] });
            }}
          >
            <option>Owner</option>
            <option>Backup</option>
          </select>
        ) : (
          <button
            type="button"
            className="hover:bg-muted rounded px-1 py-0.5 text-left"
            title="Change the role"
            disabled={off || retired}
            onClick={() => setEditRole(true)}
          >
            {row.role}
          </button>
        )}
      </td>
      <td>
        {note !== null ? (
          <Input
            className="h-7 text-[13px]"
            aria-label={`Note of ${row.label}`}
            maxLength={200}
            disabled={off}
            value={note}
            autoFocus
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setNote(null);
            }}
            onBlur={() => {
              if (note.trim() !== (row.note ?? "")) void save({ note: note.trim() });
              setNote(null);
            }}
          />
        ) : (
          <button
            type="button"
            className="hover:bg-muted min-h-6 w-full rounded px-1 py-0.5 text-left"
            title={row.note ? "Edit the note" : "Add a note"}
            disabled={off || retired}
            onClick={() => setNote(row.note ?? "")}
          >
            {row.note ? (
              row.note
            ) : (
              <span className="text-muted-foreground invisible text-xs group-hover:visible">Add note</span>
            )}
          </button>
        )}
      </td>
      <td>
        <Button size="xs" variant="outline" disabled={off} onClick={() => void retireOrRestore()}>
          {retired ? "Restore" : "Retire"}
        </Button>
      </td>
    </tr>
  );
}

/**
 * "+ Link": the Add list's multi-select (`PickList`). The row's suggestions on top, marked "suggested" and
 * ticked when it opens; then the customers what is typed may mean, else those holding a word of the label,
 * then the rest, open lines first (`linkPicks`). Ticks are kept across searches; a code on this row
 * already says "already linked". The footer links the ticked codes ("Link 4 codes to Belk (Julissa)",
 * Enter) or cancels (Esc); saved, the list closes; a failure keeps it open with the failed ones ticked.
 */
function LinkPicker({
  customers,
  row,
  linked,
  suggestions,
  holders,
  disabled,
  onLink,
}: {
  customers: readonly CustomerOption[];
  row: CustomerAssignmentRecord;
  linked: readonly string[];
  suggestions: readonly CustomerOption[];
  holders: ReadonlyMap<string, readonly CodeHolder[]>;
  disabled: boolean;
  /** Saves the ticked ones; answers the keys not saved (none: the list closes), or null when cancelled. */
  onLink: (picks: readonly CustomerPick[]) => Promise<readonly string[] | null>;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [ticked, setTicked] = useState<readonly CustomerPick[]>([]);
  const [hideNoOpen, setHideNoOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const offered = useMemo(() => (open ? customers.filter(offeredToLink) : []), [customers, open]);
  const { picks, suggested } = open
    ? linkPicks(offered, { label: row.label, suggestions, typed })
    : { picks: [], suggested: [] };
  const rows = pickRows(visiblePicks(picks, hideNoOpen), {
    mine: new Set(linked),
    holders,
    personEmail: row.personEmail,
    suggested: new Set(suggested.map((p) => p.id)),
  });
  const codes = codesOfPicks(ticked);
  const toggle = (r: PickRow) => {
    if (!r.already) setTicked((t) => togglePick(t, r.pick));
  };
  const link = async () => {
    if (!codes.length || busy) return;
    setBusy(true);
    try {
      const failed = await onLink(ticked);
      if (failed === null) return;
      if (failed.length) setTicked((t) => picksLeft(t, failed));
      else setOpen(false);
    } finally {
      setBusy(false);
    }
  };
  const nav = usePickNav(rows, { onTick: toggle, onEnter: () => void link(), onEscape: () => setOpen(false) });
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          // Opened: the suggestions start ticked, nothing typed.
          setTyped("");
          setTicked(linkPicks([], { label: row.label, suggestions, typed: "" }).suggested);
          nav.reset();
        }
      }}
    >
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={disabled}
            className="text-primary hover:bg-muted inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs"
          >
            <PlusIcon className="size-3" />
            Link
          </button>
        }
      />
      <PopoverContent align="start" className="w-[28rem] gap-1.5 p-2 text-[13px]">
        <Input
          className="h-7 text-xs"
          placeholder="Search customers — name or code"
          aria-label="Search customers to link"
          value={typed}
          disabled={busy}
          onChange={(e) => {
            setTyped(e.target.value);
            nav.reset();
          }}
          onKeyDown={nav.onKeyDown}
          autoFocus
        />
        <PickList
          rows={rows}
          ticked={ticked}
          at={nav.at}
          onAt={nav.setAt}
          onToggle={toggle}
          onSelectAll={() =>
            setTicked((t) =>
              selectAllShown(
                t,
                rows.filter((r) => !r.already).map((r) => r.pick),
              ),
            )
          }
          onClear={() => setTicked([])}
          hideNoOpen={hideNoOpen}
          onHideNoOpen={setHideNoOpen}
          alreadyText="already linked"
          testId="link-picks"
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy || !codes.length} onClick={() => void link()} data-testid="link-ticked">
            {busy ? `Linking ${codes.length}…` : linkTickedText(codes.length, row)}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Add: a person — picked from those who can use the app (`PersonPicker`), or typed as an email not in the
 * list yet — and, optionally, customers ticked in the Customer list (`CustomerBox`, a multi-select: the
 * ticks kept across searches as chips). Two or more are saved under one label by default ("One
 * assignment", the first chip's shown name, editable) or one each ("One per customer"); text typed and
 * nothing ticked is a label to confirm; with neither, the person alone ("0 customers"). One save each
 * (`addedAssignments`); saved, the chips clear and the form stays on the same person for more.
 */
function AddAssignment({
  assignments,
  customers,
}: {
  assignments: readonly CustomerAssignmentRecord[];
  customers: readonly CustomerOption[];
}) {
  const w = useAssignmentWrites();
  const message = useMessage();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  // Filled by a pick (it follows the email), or typed by hand (it stays).
  const [name, setName] = useState({ value: "", byHand: false });
  const [typed, setTyped] = useState("");
  const [picks, setPicks] = useState<readonly CustomerPick[]>([]);
  const [mode, setMode] = useState<AddMode>("one");
  // "One assignment"'s label as typed; null: the first chip's shown name.
  const [label, setLabel] = useState<string | null>(null);
  const [role, setRole] = useState<Input["role"]>("Owner");
  const [busy, setBusy] = useState(false);
  // A save failed in part: the button says Retry until the chips change.
  const [retry, setRetry] = useState(false);
  const holders = useMemo(() => codeHolders(assignments), [assignments]);
  const offered = useMemo(() => customers.filter(offeredToLink), [customers]);
  // Someone on the page already keeps the name the page shows them by: adding never renames them.
  const pageName = isEmail(emailKey(email)) ? pageNameOf(assignments, email) : undefined;
  const person = { email: emailKey(email), name: (pageName ?? name.value).trim() };
  const ready = isEmail(person.email) && !!person.name;
  const mine = useMemo(() => keysOfPerson(assignments, person.email), [assignments, person.email]);
  const shownLabel = label ?? picks[0]?.label ?? "";
  // Text typed with nothing ticked is a search: a label only when nothing matches, or on purpose (the link).
  const action = addAction({
    picks: picks.length,
    mode,
    person: person.name,
    role,
    typed,
    matches: customerPicks(offered, typed, 50).length,
    saves: 1,
  });
  const inputsFor = (typedAsLabel: boolean) =>
    ready
      ? addedAssignments({ ...person, role, picks, typed, assignments, mode, label: shownLabel, typedAsLabel })
      : [];
  const toAdd = inputsFor(action.kind === "label");
  const onPage = ready && !toAdd.length && !typed.trim();
  const buttonText =
    action.kind === "add"
      ? addButtonText({ picks: picks.length, mode, person: person.name, role, typed, saves: toAdd.length })
      : action.text;
  const setTicks = (next: readonly CustomerPick[]) => {
    setPicks(next);
    setRetry(false);
    if (!next.length) setLabel(null);
  };
  const add = async (inputs: readonly NewAssignment[] = toAdd) => {
    setBusy(true);
    try {
      let into = personOnlyRow(assignments, person.email);
      const r = await saveEach(
        inputs,
        async (input) => {
          await w.add(input, into);
          into = undefined;
        },
        crudErrorText,
      );
      if (r.failed.length) {
        await message.error(
          `Added ${r.done.length} · ${r.failed.length} failed`,
          "The customers not saved are still ticked: Retry saves them.",
          r.failed.map((f) => `${f.item.label || person.name} — ${f.why}`),
        );
        // Only what failed stays to add again: one per customer drops the saved ones.
        setPicks((p) =>
          picksLeft(
            p,
            r.failed.map((f) => f.item.customerKeys ?? ""),
          ),
        );
        setRetry(true);
        return;
      }
      if (inputs.length === 1 && !inputs[0]!.label)
        notify(`${person.name} added`, "No customers yet: add some from No owner, or + Link on their row.");
      else if (picks.length) notify(addedText({ picks: picks.length, mode, person: person.name, role }));
      else notify(`Added “${typed.trim()}” to ${person.name} (${role})`, "It waits under Needs confirming.");
      // Ready for more on the same person.
      setTyped("");
      setTicks([]);
      setMode("one");
    } finally {
      setBusy(false);
      await w.refresh();
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button size="sm" variant="outline" className="gap-1">
            <PlusIcon className="size-3.5" />
            Add
          </Button>
        }
      />
      {/* No taller than the window: it scrolls inside itself, the Add button pinned at its foot. */}
      <PopoverContent
        align="end"
        className="max-h-(--available-height) w-[26rem] gap-2 overflow-y-auto p-3 text-[13px]"
        data-testid="add-form"
      >
        <fieldset disabled={busy} className="contents">
          <PersonPicker
            enabled={open}
            email={email}
            onEmail={(t, known) => {
              setEmail(t);
              setName((n) => nameAfterEmail(n, known));
            }}
            onPick={(p) => {
              setEmail(p.email);
              setName({ value: p.name, byHand: false });
            }}
          />
          <Input
            className="h-8"
            placeholder="Name"
            aria-label="Name"
            value={pageName ?? name.value}
            // On the page already: the name it shows them by, kept (renaming isn't done here).
            disabled={!!pageName}
            title={pageName ? `As on the page: ${pageName}` : undefined}
            onChange={(e) => setName({ value: e.target.value, byHand: e.target.value.trim() !== "" })}
          />
          <CustomerBox
            customers={customers}
            typed={typed}
            onType={setTyped}
            picks={picks}
            onPicks={setTicks}
            mine={mine}
            holders={holders}
            person={{ email: person.email, name: person.name }}
          />
          {action.labelLink && ready ? (
            <button
              type="button"
              className="text-primary self-start text-xs hover:underline"
              onClick={() => void add(inputsFor(true))}
              data-testid="add-as-label"
            >
              {action.labelLink}
            </button>
          ) : null}
          {picks.length > 1 ? (
            <div className="space-y-1 rounded-md border p-2 text-xs" data-testid="add-mode">
              <label className="flex items-center gap-1.5">
                <input type="radio" name="add-mode" checked={mode === "one"} onChange={() => setMode("one")} />
                <span className="font-medium">One assignment</span>
                <span className="text-muted-foreground">— every code under one label</span>
              </label>
              {mode === "one" ? (
                <Input
                  className="ml-5 h-7 w-auto text-[13px]"
                  aria-label="Label of the assignment"
                  maxLength={200}
                  value={shownLabel}
                  onChange={(e) => setLabel(e.target.value)}
                />
              ) : null}
              <label className="flex items-center gap-1.5">
                <input type="radio" name="add-mode" checked={mode === "each"} onChange={() => setMode("each")} />
                <span className="font-medium">One per customer</span>
                <span className="text-muted-foreground">— each labelled with its shown name</span>
              </label>
            </div>
          ) : null}
          <select
            className="border-input bg-background h-8 rounded-md border px-2"
            aria-label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as Input["role"])}
          >
            <option>Owner</option>
            <option>Backup</option>
          </select>
          {onPage ? (
            <p className="text-muted-foreground text-xs">
              {person.name} is on the page already: tick a customer, or type one.
            </p>
          ) : null}
          <div className="bg-popover sticky -bottom-3 -mx-3 -mb-3 border-t px-3 py-2">
            <Button
              size="sm"
              className="w-full"
              disabled={
                busy ||
                action.kind === "tick" ||
                !toAdd.length ||
                (mode === "one" && picks.length > 1 && !shownLabel.trim())
              }
              onClick={() => void add()}
              data-testid="add-assignment"
            >
              {busy ? "Adding…" : retry ? `Retry — ${buttonText}` : buttonText}
            </Button>
          </div>
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Add's Customer list (optional), the multi-select (`PickList`): from 2 characters the customers what is
 * typed may mean — shown names first (one row, every code under it), then original names and codes
 * (`customerPicks`). A click, Enter, or Space on a row reached with ↑/↓ ticks or unticks it; the list stays
 * open and the text stays; ticks are kept across searches as chips (× removes one, Backspace in the empty
 * box the last); Esc closes the list. A code the person links already says "already Shai's".
 */
function CustomerBox({
  customers,
  typed,
  onType,
  picks,
  onPicks,
  mine,
  holders,
  person,
}: {
  customers: readonly CustomerOption[];
  typed: string;
  onType: (v: string) => void;
  picks: readonly CustomerPick[];
  onPicks: (p: readonly CustomerPick[]) => void;
  mine: ReadonlySet<string>;
  holders: ReadonlyMap<string, readonly CodeHolder[]>;
  person: { email: string; name: string };
}) {
  const [listOpen, setListOpen] = useState(false);
  const [hideNoOpen, setHideNoOpen] = useState(false);
  const offered = useMemo(() => customers.filter(offeredToLink), [customers]);
  const rows = pickRows(visiblePicks(customerPicks(offered, typed, 50), hideNoOpen), {
    mine,
    holders,
    personEmail: person.email,
  });
  const toggle = (r: PickRow) => {
    if (!r.already) onPicks(togglePick(picks, r.pick));
  };
  const nav = usePickNav(rows, { onTick: toggle, onEscape: () => setListOpen(false) });
  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          className="h-8"
          placeholder="Customers (optional): a shown name, a name or a code"
          aria-label="Customer"
          role="combobox"
          aria-expanded={listOpen && rows.length > 0}
          aria-autocomplete="list"
          value={typed}
          onFocus={() => setListOpen(true)}
          onBlur={() => setListOpen(false)}
          onChange={(e) => {
            onType(e.target.value);
            nav.reset();
            setListOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !typed && picks.length) return onPicks(picks.slice(0, -1));
            if (e.key === "Escape" && !listOpen) return;
            if (listOpen || e.key === "Escape") nav.onKeyDown(e);
          }}
        />
        {listOpen && typed.trim().length >= 2 ? (
          <PickList
            className="mt-1 shadow-sm"
            rows={rows}
            ticked={picks}
            at={nav.at}
            onAt={nav.setAt}
            onToggle={toggle}
            onSelectAll={() =>
              onPicks(
                selectAllShown(
                  picks,
                  rows.filter((r) => !r.already).map((r) => r.pick),
                ),
              )
            }
            onClear={() => onPicks([])}
            hideNoOpen={hideNoOpen}
            onHideNoOpen={setHideNoOpen}
            alreadyText={person.name ? `already ${person.name}'s` : "already theirs"}
            testId="customer-picks"
          />
        ) : null}
      </div>
      <PickChips picks={picks} onRemove={(id) => onPicks(picks.filter((x) => x.id !== id))} />
    </div>
  );
}

/** "Manager · signed in 10/29": what the app knows of a person. */
const personTags = (p: PersonOption) =>
  [
    ...p.roles,
    p.lastSeen ? `signed in ${new Date(p.lastSeen).toLocaleDateString([], { month: "2-digit", day: "2-digit" })}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * The email field with the people who can use the app: typing matches a name or an email anywhere
 * ("dijana", "icov"), ↑/↓ and Enter pick, a click picks; picking fills the email and the name. A typed
 * email not in the list stays allowed, with a quiet note.
 */
function PersonPicker({
  enabled,
  exclude,
  email,
  onEmail,
  onPick,
}: {
  enabled: boolean;
  /** Left out of the list (Assign Ana's customers: Ana). */
  exclude?: string;
  email: string;
  /** The typed email, and the known person it is (`personOfEmail`), if any. */
  onEmail: (email: string, known: PersonOption | undefined) => void;
  onPick: (p: PersonOption) => void;
}) {
  const people = useQuery({
    queryKey: PEOPLE_KEY,
    queryFn: ({ signal }) => apiGet<PeoplePayload>(`${ROUTING_API}/admin/people`, signal),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const [listOpen, setListOpen] = useState(false);
  // The highlighted match: arrows move it, Enter picks it.
  const [at, setAt] = useState(0);
  const all = (people.data?.people ?? []).filter((p) => !exclude || p.email !== emailKey(exclude));
  const typed = emailKey(email);
  const known = personOfEmail(all, email);
  const matches = all.filter((p) => personMatches(p, email)).slice(0, 8);
  const pick = (p: PersonOption) => {
    onPick(p);
    setListOpen(false);
  };
  return (
    <div className="relative space-y-1">
      <Input
        className="h-8"
        placeholder="Person: a name or an email"
        aria-label="Person's email"
        role="combobox"
        aria-expanded={listOpen && matches.length > 0}
        aria-autocomplete="list"
        value={email}
        onFocus={() => setListOpen(true)}
        onBlur={() => setListOpen(false)}
        onChange={(e) => {
          onEmail(e.target.value, personOfEmail(all, e.target.value));
          setAt(0);
          setListOpen(true);
        }}
        onKeyDown={(e) => {
          if (!listOpen || !matches.length) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setAt((i) => (i + (e.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(matches[Math.min(at, matches.length - 1)]!);
          } else if (e.key === "Escape") setListOpen(false);
        }}
      />
      {listOpen && matches.length > 0 && !(known && known.email === typed && matches.length === 1) ? (
        <div role="listbox" className="bg-background max-h-64 overflow-y-auto rounded-md border p-1 shadow-sm">
          {matches.map((p, i) => (
            <button
              key={p.email}
              type="button"
              role="option"
              aria-selected={i === at}
              className={cn(
                "hover:bg-muted flex w-full flex-col items-start rounded px-1.5 py-1 text-left",
                i === at && "bg-muted",
              )}
              onMouseEnter={() => setAt(i)}
              // Before the field's blur closes the list.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
            >
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground text-xs">
                {p.email}
                {p.also ? ` · also ${p.also}` : ""}
                {personTags(p) ? ` · ${personTags(p)}` : ""}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {known ? (
        <p className="text-muted-foreground text-xs">{[known.name, personTags(known)].filter(Boolean).join(" · ")}</p>
      ) : isEmail(typed) ? (
        <p className="text-muted-foreground text-xs">Not in the app's groups yet</p>
      ) : null}
      {people.data?.groups === "not permitted" ? (
        <p className="text-muted-foreground text-xs">
          The role groups can't be read: showing those who signed in or are assigned.
        </p>
      ) : null}
    </div>
  );
}
